import React from 'react';
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage
} from '@mui/icons-material';

interface CustomPaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: SelectChangeEvent<number>) => void;
  rowsPerPageOptions?: number[];
  labelRowsPerPage?: string;
  labelDisplayedRows?: (info: { from: number; to: number; count: number }) => string;
}

/**
 * CustomPagination - 自定义分页组件
 * 提供更美观的分页UI，替代默认的TablePagination
 */
const CustomPagination: React.FC<CustomPaginationProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  labelRowsPerPage = '每页行数:',
  labelDisplayedRows = ({ from, to, count }) => `${from}-${to} 共 ${count} 条`
}) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);

  const handleFirstPageClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handlePreviousPageClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextPageClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, totalPages - 1);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    onRowsPerPageChange(event);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        flexWrap: 'wrap',
        gap: 2
      }}
    >
      {/* 每页行数选择 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {labelRowsPerPage}
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
          size="small"
          sx={{
            minWidth: 70,
            bgcolor: 'white',
            borderRadius: 1.5,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
              borderWidth: 1
            }
          }}
        >
          {rowsPerPageOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* 显示的行数信息 */}
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ 
          display: { xs: 'none', sm: 'block' },
          fontWeight: 500
        }}
      >
        {labelDisplayedRows({ from, to, count })}
      </Typography>

      {/* 分页按钮 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          onClick={handleFirstPageClick}
          disabled={page === 0}
          size="small"
          sx={{
            borderRadius: 1.5,
            '&:hover': {
              bgcolor: 'primary.50'
            },
            '&.Mui-disabled': {
              bgcolor: 'transparent'
            }
          }}
        >
          <FirstPage fontSize="small" />
        </IconButton>
        
        <IconButton
          onClick={handlePreviousPageClick}
          disabled={page === 0}
          size="small"
          sx={{
            borderRadius: 1.5,
            '&:hover': {
              bgcolor: 'primary.50'
            },
            '&.Mui-disabled': {
              bgcolor: 'transparent'
            }
          }}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>

        {/* 页码显示 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 80,
            px: 2,
            py: 0.5,
            mx: 1,
            bgcolor: 'white',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {page + 1} / {totalPages || 1}
          </Typography>
        </Box>

        <IconButton
          onClick={handleNextPageClick}
          disabled={page >= totalPages - 1}
          size="small"
          sx={{
            borderRadius: 1.5,
            '&:hover': {
              bgcolor: 'primary.50'
            },
            '&.Mui-disabled': {
              bgcolor: 'transparent'
            }
          }}
        >
          <ChevronRight fontSize="small" />
        </IconButton>

        <IconButton
          onClick={handleLastPageClick}
          disabled={page >= totalPages - 1}
          size="small"
          sx={{
            borderRadius: 1.5,
            '&:hover': {
              bgcolor: 'primary.50'
            },
            '&.Mui-disabled': {
              bgcolor: 'transparent'
            }
          }}
        >
          <LastPage fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CustomPagination;

